/**
 * Leaguemate Scouting — per-member, on-demand orchestration (S4 facet model).
 *
 * Composes the existing S1–S3.5 modules for a SINGLE member, lazily, so each
 * drill-down facet pulls only what it needs (info-first, per-member). NOTHING
 * here reimplements scoring — it wires the proven compute together:
 *   Other leagues  → fetchLeaguemateLeagueSets (raw list, categorized in UI)
 *   Foundation     → rankAndCapLeagues + per-league rosters + buildManagerBelievers
 *   Trades/Waivers → selectTxCandidateSet + fetchTransactionsForSet (raw completeTx)
 *   Fingerprint    → buildOwnerDeepDive (the only computed surface)
 *
 * Trades / Waivers / Fingerprint all read /transactions, so loading one warms
 * the others via scoutFetch's memo + IndexedDB cache.
 */

import { getSleeperPlayersMap } from "../sleeperImport.js";
import { scoutFetch, SCOUT_BASE_URL } from "./scoutFetch.js";
import { fetchLeaguemateLeagueSets } from "./scoutDiscovery.js";
import { rankAndCapLeagues } from "./scoutSimilarity.js";
import { buildManagerBelievers } from "./scoutAffinity.js";
import { fetchValueMap } from "./scoutValues.js";
import { selectTxCandidateSet, fetchTransactionsForSet } from "./scoutDeepFetch.js";
import { buildOwnerDeepDive } from "./scoutDeepDive.js";

const noop = () => {};

/** Facet 1 — their OTHER NFL leagues (current + prior, deduped). ~1–2 calls. */
export async function fetchMemberOtherLeagues(userId, { onProgress = noop } = {}) {
  const map = await fetchLeaguemateLeagueSets([userId], { onProgress });
  return map.get(userId) || [];
}

/**
 * Build the affinity foundation for ONE member: rank their other leagues by
 * format similarity, pull their roster in each sampled league, score believers.
 * This is the per-member equivalent of buildScoutFoundation's manager body —
 * assembled from the same exported compute, scoped to one member.
 *
 * @returns { sampled, counts, affinity, believers, affinityMeta }
 */
export async function buildMemberFoundation(member, target, { playersMap = null, valueMap = null, otherLeagues = null, onProgress = noop } = {}) {
  const leagues = (otherLeagues || (await fetchMemberOtherLeagues(member.user_id, { onProgress })))
    .filter((l) => l.league_id !== target.league_id); // their *other* leagues

  const { sampled, counts } = rankAndCapLeagues(target.profile, leagues);

  // Pull the member's roster in each sampled league (parallel; throttle queue
  // in scoutFetch governs pacing). Reuses cached rosters where warmed.
  const total = sampled.length;
  let pulled = 0;
  const affinity = await Promise.all(
    sampled.map(async (s) => {
      const season = s.league.season || target.season;
      const ls = s.league.settings || {};
      const rosters = await scoutFetch(`${SCOUT_BASE_URL}/league/${s.league_id}/rosters`, {
        cacheId: s.league_id, season, dataType: "scout_rosters",
      });
      const arr = Array.isArray(rosters) ? rosters : [];
      const mine = arr.find((r) => r.owner_id === member.user_id);
      onProgress(`Roster ${++pulled}/${total}`, pulled, total);
      return {
        league_id: s.league_id,
        previous_league_id: s.league.previous_league_id ?? null,
        league_name: s.league.name || null,
        season,
        similarity: s.similarity,
        inTxSet: s.inTxSet,
        leagueType: ls.type ?? null,
        bestBall: ls.best_ball === 1,
        waiverBudget: ls.waiver_budget ?? 0,
        roster_id: mine?.roster_id ?? null,
        players: mine?.players || [],
        found: !!mine,
      };
    })
  );

  const pmap = playersMap || (await getSleeperPlayersMap());
  const vmap = valueMap || (await fetchValueMap(target.profile)).valueMap;
  const aff = buildManagerBelievers(affinity, pmap, vmap);

  return {
    sampled: sampled.map((s) => ({
      league_id: s.league_id, league_name: s.league.name || null,
      season: s.league.season || target.season, similarity: s.similarity, inTxSet: s.inTxSet,
      leagueType: (s.league.settings || {}).type ?? null, bestBall: (s.league.settings || {}).best_ball === 1,
    })),
    counts,
    affinity,
    believers: aff.believers,
    affinityMeta: {
      scope: "believers-only",
      franchiseCount: aff.franchiseCount,
      usableFranchiseCount: aff.usableFranchiseCount,
      limitedData: aff.limitedData,
      confidence: aff.confidence,
    },
  };
}

/** Facets 2/3 — raw transactions over the member's dynasty/keeper TX set. */
export async function fetchMemberTransactions(affinity, { onProgress = noop } = {}) {
  const txSet = selectTxCandidateSet(affinity);
  return fetchTransactionsForSet(txSet, { onProgress });
}

export { buildOwnerDeepDive };
